import { useState, useEffect, useCallback, useRef } from 'react';
import { AlertCircle, CheckCircle, Info, Lightbulb, MessageSquare } from 'lucide-react';
import { motion } from 'framer-motion';
import { debounce } from '@/lib/utils';

interface FeedbackTip {
  type: 'warning' | 'error' | 'praise' | 'guidance' | 'coaching';
  message: string;
  timestamp: Date;
  stage?: string; // Optional: Sales stage (e.g., "needs_discovery", "objection_handling")
  // To track which message this feedback corresponds to
  messageIndex: number;
}

interface InlineRealtimeFeedbackProps {
  transcript: Array<{
    source: 'ai' | 'user';
    message: string;
    timestamp: Date;
  }>;
  scenarioCode: string;
  language: 'en' | 'ko' | 'ja';
  // This will be used to inject feedback tips
  setFeedbackTips: React.Dispatch<React.SetStateAction<FeedbackTip[]>>;
  // Optional: Disable feedback (for testing or if backend is down)
  enabled?: boolean;
}

/**
 * Insurance Roleplay Inline Feedback Component
 *
 * This component analyzes the conversation in real-time and provides
 * contextual feedback to help the trainee improve their sales skills.
 *
 * Key Features:
 * - Real-time analysis after each user message
 * - Debounced API calls to avoid overload (2 second delay)
 * - Feedback types: warning, error, praise, guidance, coaching
 * - Inline display under user messages
 *
 * Safety & Non-Blocking Design:
 * - AbortController cancels pending requests on new messages or unmount
 * - Duplicate message detection prevents re-processing
 * - All errors are caught and logged (never thrown to UI)
 * - Feedback failures are silent - main conversation flow never breaks
 * - Can be disabled via `enabled` prop for testing or graceful degradation
 * - Runs completely in background without blocking user interaction
 */
export function InlineRealtimeFeedback({
  transcript,
  scenarioCode,
  language,
  setFeedbackTips,
  enabled = true, // Default to enabled
}: InlineRealtimeFeedbackProps) {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const processedMessagesRef = useRef<Set<number>>(new Set());

  // API base URL from environment
  const API_BASE_URL = import.meta.env.VITE_API_BASE || 'https://aibot.hupo.co';

  // Cleanup on unmount - abort any pending requests
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        console.log('🧹 Aborted pending feedback request on unmount');
      }
    };
  }, []);

  // Debounced function to avoid too many API calls
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const analyzeTranscript = useCallback(
    debounce(async (transcript, latestMessageIndex) => {
      if (transcript.length === 0) return;

      // Skip if we've already processed this message
      if (processedMessagesRef.current.has(latestMessageIndex)) {
        console.log('⏭️ Already processed message index:', latestMessageIndex);
        return;
      }

      // Only analyze if the latest message is from the user and there's at least one AI message
      const latestMessage = transcript[latestMessageIndex];
      const hasAIMessages = transcript.some((msg) => msg.source === 'ai');

      if (!latestMessage || latestMessage.source !== 'user' || !hasAIMessages) {
        return;
      }

      // Abort previous request if still pending
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        console.log('🛑 Aborted previous feedback request');
      }

      // Create new abort controller for this request
      abortControllerRef.current = new AbortController();

      try {
        setIsAnalyzing(true);
        console.log('🔄 Starting inline feedback analysis for message index:', latestMessageIndex);

        // Format transcript for API
        const conversationHistory = transcript.map((msg) => ({
          source: msg.source,
          message: msg.message,
          timestamp: msg.timestamp.toISOString(),
        }));

        // Make API request with abort controller
        const response = await fetch(
          `${API_BASE_URL}/api/roleplay/feedback/analyze`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              scenarioCode,
              conversationHistory,
              language,
            }),
            signal: abortControllerRef.current.signal,
          }
        );

        if (!response.ok) {
          // Silently fail - don't break the UI
          const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
          console.warn('⚠️ Feedback API error (non-critical):', errorData);
          return;
        }

        const analysis: {
          feedbackTips: Array<{
            type: 'warning' | 'error' | 'praise' | 'guidance' | 'coaching';
            message: string;
            messageKo?: string; // Korean translation
            stage?: string;
          }>;
        } = await response.json();

        console.log('✅ Inline feedback API response:', analysis);

        if (analysis?.feedbackTips?.length > 0) {
          // Filter out any invalid types
          const validTips = analysis.feedbackTips.filter((tip) =>
            ['warning', 'error', 'praise', 'guidance', 'coaching'].includes(tip.type),
          );

          const newTips = validTips.map((tip) => ({
            type: tip.type,
            message: (language === 'ko' || language === 'ja') && tip.messageKo ? tip.messageKo : tip.message,
            timestamp: new Date(),
            messageIndex: latestMessageIndex,
            stage: tip.stage,
          })) as FeedbackTip[];

          // Update the feedback tips state (non-blocking)
          setFeedbackTips((prev) => [...prev, ...newTips]);

          // Mark this message as processed
          processedMessagesRef.current.add(latestMessageIndex);

          console.log('✅ Added inline feedback tips for message:', latestMessageIndex);
        }
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          console.log('⚠️ Feedback request aborted');
          return;
        }
        // Silently log errors - don't break the UI
        console.warn('⚠️ Error getting inline feedback (non-critical):', error);
      } finally {
        setIsAnalyzing(false);
        abortControllerRef.current = null;
      }
    }, 2000), // 2 second debounce
    [scenarioCode, language, API_BASE_URL],
  );

  // Monitor for new user messages
  useEffect(() => {
    // Skip if disabled
    if (!enabled) {
      console.log('⏸️ Feedback analysis disabled');
      return;
    }

    if (transcript.length === 0) return;

    // Only proceed if there's at least one AI message in the transcript
    const hasAIMessages = transcript.some((msg) => msg.source === 'ai');
    if (!hasAIMessages) return;

    // Check if the last message is from the user
    const lastMessage = transcript[transcript.length - 1];
    if (lastMessage.source !== 'user') return;

    // Find the index of this last user message
    const lastUserMessageIndex = transcript.length - 1;

    console.log('📝 Detected new user message at index:', lastUserMessageIndex);

    // Use try-catch to ensure this doesn't break the main flow
    try {
      analyzeTranscript(transcript, lastUserMessageIndex);
    } catch (error) {
      console.warn('⚠️ Error triggering feedback analysis (non-critical):', error);
    }
  }, [transcript, analyzeTranscript, enabled]);

  // This component doesn't render anything directly
  return null;
}

// Component to render an inline feedback tip
export function InlineFeedbackTip({
  tip,
  delay = 0,
}: {
  tip: FeedbackTip;
  delay?: number;
}) {
  const getIcon = () => {
    switch (tip.type) {
      case 'warning':
        return <Info className="w-4 h-4 text-blue-500" />;
      case 'error':
        return <AlertCircle className="w-4 h-4 text-red-600" />;
      case 'praise':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'guidance':
        return <Lightbulb className="w-4 h-4 text-purple-500" />;
      case 'coaching':
        return <MessageSquare className="w-4 h-4 text-blue-600" />;
      default:
        return null;
    }
  };

  const getColor = () => {
    switch (tip.type) {
      case 'warning':
        return 'border-blue-300 text-blue-700 bg-blue-50';
      case 'error':
        return 'border-red-300 text-red-800 bg-red-50';
      case 'praise':
        return 'border-green-300 text-green-700 bg-green-50';
      case 'guidance':
        return 'border-purple-300 text-purple-700 bg-purple-50';
      case 'coaching':
        return 'border-blue-400 text-blue-800 bg-blue-50';
      default:
        return 'border-slate-300 text-slate-700 bg-slate-50';
    }
  };

  // Clean the message of markdown formatting
  const cleanMessage = (message: string) => {
    // Remove markdown elements:
    // - Bold/italic markings: ** or __ or * or _
    // - Headers: # at beginning of line
    // - Feedback/warning prefixes
    return message
      .replace(/\*\*|\*|__|_/g, '') // Remove bold/italic
      .replace(/^#+\s*/g, '') // Remove headers
      .replace(/^Feedback:\s*/i, '') // Remove "Feedback:" prefix
      .replace(/^Warning:\s*/i, '') // Remove "Warning:" prefix
      .replace(/^Error:\s*/i, '') // Remove "Error:" prefix
      .replace(/^Praise:\s*/i, '') // Remove "Praise:" prefix
      .trim();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 0, x: 10 }}
      animate={{ opacity: 1, y: 0, x: 0 }}
      exit={{ opacity: 0, x: 10 }}
      transition={{
        duration: 0.25,
        type: 'spring',
        stiffness: 120,
        damping: 14,
        delay: delay,
      }}
      className="flex justify-end my-1.5 relative"
    >
      <div
        className={`relative max-w-[80%] inline-flex items-start rounded-md border-l-[3px] ${getColor()} py-1.5 pl-2 pr-3 shadow-[0_1px_2px_rgba(0,0,0,0.05)]`}
        style={{ marginRight: 'calc(1rem + 8px)' }} // Align with user message
      >
        <div className="flex-shrink-0 mt-[3px] mr-1.5">{getIcon()}</div>
        <div className="flex-1 min-w-0">
          <p
            className={`text-xs ${
              getColor().split(' ')[1]
            } break-words leading-relaxed font-medium`}
          >
            {cleanMessage(tip.message)}
          </p>
        </div>
      </div>
    </motion.div>
  );
}

// Export the FeedbackTip type for use in other components
export type { FeedbackTip };
